import { useState, useCallback } from 'react';
import type { WizardStep, WizardState } from '../types/WizardTypes';

interface UseWizardStateProps {
  steps: WizardStep[];
  initialStep: number;
  initialData: Record<string, any>;
}

interface UseWizardStateReturn {
  state: WizardState;
  setState: React.Dispatch<React.SetStateAction<WizardState>>;
  setCurrentStep: (step: number) => void;
  setStepData: (stepId: string, data: any) => void;
  setStepCompleted: (stepId: string, completed: boolean) => void;
  setStepErrors: (stepId: string, errors: string[]) => void;
  setGlobalErrors: (errors: string[]) => void;
  clearStepErrors: (stepId: string) => void;
  resetWizard: () => void;
}

/**
 * Hook for managing wizard state
 * Provides state management utilities for the FormWizard component
 */
export function useWizardState({
  steps,
  initialStep,
  initialData
}: UseWizardStateProps): UseWizardStateReturn {
  
  // Initialize state
  const [state, setState] = useState<WizardState>(() => {
    const stepData: Record<string, any> = {};
    const stepCompleted: Record<string, boolean> = {};
    const stepErrors: Record<string, string[]> = {};

    // Initialize data structures for each step
    steps.forEach(step => {
      stepData[step.id] = initialData[step.id] || {};
      stepCompleted[step.id] = false;
      stepErrors[step.id] = [];
    });

    return {
      currentStep: Math.max(0, Math.min(initialStep, steps.length - 1)),
      stepData,
      stepErrors,
      stepCompleted,
      isLoading: false,
      globalErrors: []
    };
  });

  // Setters for individual state properties
  const setCurrentStep = useCallback((step: number) => {
    setState(prevState => ({
      ...prevState,
      currentStep: Math.max(0, Math.min(step, steps.length - 1))
    }));
  }, [steps.length]);

  const setStepData = useCallback((stepId: string, data: any) => {
    setState(prevState => ({
      ...prevState,
      stepData: {
        ...prevState.stepData,
        [stepId]: data
      }
    }));
  }, []);

  const setStepCompleted = useCallback((stepId: string, completed: boolean) => {
    setState(prevState => ({
      ...prevState,
      stepCompleted: {
        ...prevState.stepCompleted,
        [stepId]: completed
      }
    }));
  }, []);

  const setStepErrors = useCallback((stepId: string, errors: string[]) => {
    setState(prevState => ({
      ...prevState,
      stepErrors: {
        ...prevState.stepErrors,
        [stepId]: errors
      }
    }));
  }, []);

  const clearStepErrors = useCallback((stepId: string) => {
    setState(prevState => ({
      ...prevState,
      stepErrors: {
        ...prevState.stepErrors,
        [stepId]: []
      }
    }));
  }, []);

  const setGlobalErrors = useCallback((errors: string[]) => {
    setState(prevState => ({
      ...prevState,
      globalErrors: errors
    }));
  }, []);

  const resetWizard = useCallback(() => {
    setState(prevState => {
      const stepData: Record<string, any> = {};
      const stepCompleted: Record<string, boolean> = {};
      const stepErrors: Record<string, string[]> = {};

      steps.forEach(step => {
        stepData[step.id] = {};
        stepCompleted[step.id] = false;
        stepErrors[step.id] = [];
      });

      return {
        currentStep: initialStep,
        stepData,
        stepErrors,
        stepCompleted,
        isLoading: false,
        globalErrors: []
      };
    });
  }, [steps, initialStep]);

  return {
    state,
    setState,
    setCurrentStep,
    setStepData,
    setStepCompleted,
    setStepErrors,
    setGlobalErrors,
    clearStepErrors,
    resetWizard
  };
}